import { useHandler } from "@redotech/react-util/hook";
import {
  AfterTimeframeCondition,
  BeforeNowRelativeTimeframeCondition,
  BeforeTimeframeCondition,
  BetweenDatesTimeframeCondition,
  BetweenRelativeTimeframeCondition,
  FullDateTimeframeCondition,
  fullDateTimeframeConditionTypes,
  OnTimeframeCondition,
  TimeframeConditionType,
  TimeframeUnit,
} from "@redotech/redo-model/marketing/segments/segment-timeframe";
import {
  dateToPlainDateTime,
  plainDateTimeToDate,
} from "@redotech/util/temporal";
import { assertNever } from "@redotech/util/type";
import { FC, memo, useContext, useMemo } from "react";
import { RedoDateRangePicker } from "../../../arbiter-components/date-picker/redo-date-range-picker";
import { RedoSingleDatePicker } from "../../../arbiter-components/date-picker/redo-single-date-picker";
import { RedoListItem } from "../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  SegmentBuilderLocationContext,
  SegmentEditorLocation,
} from "../segment-builder-editor";
import { SegmentBuilderText } from "../segment-builder-text";
import { FullDateTimeFrameConditionInProgress as InProgress } from "../segment-in-progress";
import { NumericFilterInput } from "./numeric-filter";

export const FullDateTimeframeFilter = memo(function FullDateTimeframeFilter({
  comparison,
  setComparison,
}: {
  comparison: InProgress<FullDateTimeframeCondition>;
  setComparison: (comparison: InProgress<FullDateTimeframeCondition>) => void;
}) {
  const handleConditionTypeChange = useHandler(
    (conditionType: FullDateTimeframeCondition["type"]) => {
      if (conditionType === comparison.type) {
        return;
      }
      const defaultValues = conditionTypeToDefault[conditionType];
      setComparison(defaultValues);
    },
  );

  const TimeFrameConfiguration: FC<{
    condition: any;
    setCondition: (condition: any) => any;
  }> | null = useMemo(() => {
    switch (comparison.type) {
      case TimeframeConditionType.ON:
        return OnTimeFrameSelector;
      case TimeframeConditionType.BEFORE_NOW_RELATIVE:
        return BeforeNowRelativeTimeFrameSelector;
      case TimeframeConditionType.BEFORE:
        return BeforeTimeFrameSelector;
      case TimeframeConditionType.AFTER:
        return AfterTimeFrameSelector;
      case TimeframeConditionType.ALL_TIME:
      case TimeframeConditionType.TODAY:
      case TimeframeConditionType.AUTOMATION_START:
        return null;
      case TimeframeConditionType.BETWEEN_DATES:
        return BetweenDatesTimeFrameSelector;
      case TimeframeConditionType.BETWEEN_RELATIVE:
        return BetweenRelativeTimeFrameSelector;
      case TimeframeConditionType.BEFORE_RELATIVE:
        return BeforeRelativeTimeFrameSelector;
      default:
        assertNever(comparison);
    }
  }, [comparison]);

  return (
    <>
      <FullDateTimeFrameConditionTypeSelector
        conditionType={comparison.type}
        setConditionType={handleConditionTypeChange}
      />
      {TimeFrameConfiguration && (
        <TimeFrameConfiguration
          condition={comparison}
          setCondition={setComparison}
        />
      )}
    </>
  );
});

const conditionTypeToDefault: Record<
  FullDateTimeframeCondition["type"],
  InProgress<FullDateTimeframeCondition>
> = {
  [TimeframeConditionType.BEFORE_NOW_RELATIVE]: {
    type: TimeframeConditionType.BEFORE_NOW_RELATIVE,
    units: TimeframeUnit.DAY,
  },
  [TimeframeConditionType.AFTER]: { type: TimeframeConditionType.AFTER },
  [TimeframeConditionType.BEFORE]: { type: TimeframeConditionType.BEFORE },
  [TimeframeConditionType.BETWEEN_DATES]: {
    type: TimeframeConditionType.BETWEEN_DATES,
  },
  [TimeframeConditionType.BETWEEN_RELATIVE]: {
    type: TimeframeConditionType.BETWEEN_RELATIVE,
    units: TimeframeUnit.DAY,
  },
  [TimeframeConditionType.ALL_TIME]: { type: TimeframeConditionType.ALL_TIME },
  [TimeframeConditionType.ON]: { type: TimeframeConditionType.ON },
  [TimeframeConditionType.BEFORE_RELATIVE]: {
    type: TimeframeConditionType.BEFORE_RELATIVE,
    units: TimeframeUnit.DAY,
  },
  [TimeframeConditionType.TODAY]: { type: TimeframeConditionType.TODAY },
  [TimeframeConditionType.AUTOMATION_START]: {
    type: TimeframeConditionType.AUTOMATION_START,
    automationStartTime: new Date(),
  },
};

const timeFrameConditionTypeToFriendly: Record<
  FullDateTimeframeCondition["type"],
  string
> = {
  [TimeframeConditionType.BEFORE_NOW_RELATIVE]: "in the last",
  [TimeframeConditionType.AFTER]: "after",
  [TimeframeConditionType.BEFORE]: "before",
  [TimeframeConditionType.BETWEEN_DATES]: "between dates",
  [TimeframeConditionType.BETWEEN_RELATIVE]: "between the last",
  [TimeframeConditionType.ALL_TIME]: "over all time",
  [TimeframeConditionType.ON]: "on",
  [TimeframeConditionType.BEFORE_RELATIVE]: "at least",
  [TimeframeConditionType.TODAY]: "today",
  [TimeframeConditionType.AUTOMATION_START]: "since this automation started",
};

const allFullDateTimeFrameConditionTypeOptions: RedoListItem<
  FullDateTimeframeCondition["type"]
>[] = fullDateTimeframeConditionTypes.map((type) => ({
  id: type,
  type: "text",
  text: timeFrameConditionTypeToFriendly[type],
  value: type,
}));

const FullDateTimeFrameConditionTypeSelector = memo(
  function FullDateTimeFrameConditionTypeSelector({
    conditionType,
    setConditionType,
  }: {
    conditionType: FullDateTimeframeCondition["type"];
    setConditionType: (
      conditionType: FullDateTimeframeCondition["type"],
    ) => void;
  }) {
    const location = useContext(SegmentBuilderLocationContext);

    const timeframeOptions: RedoListItem<FullDateTimeframeCondition["type"]>[] =
      useMemo(() => {
        switch (location) {
          case SegmentEditorLocation.SEGMENT_BUILDER:
            return allFullDateTimeFrameConditionTypeOptions.filter(
              (option) =>
                option.value !== TimeframeConditionType.AUTOMATION_START,
            );
          case SegmentEditorLocation.AUTOMATIONS_PANEL:
            return allFullDateTimeFrameConditionTypeOptions.filter(
              (option) => option.value !== TimeframeConditionType.TODAY,
            );
          default:
            assertNever(location);
        }
      }, [location]);

    return (
      <RedoSingleSelectDropdownInput<FullDateTimeframeCondition["type"]>
        fitToAnchor={false}
        flexGrow={false}
        options={timeframeOptions}
        optionSelected={({ item }) => setConditionType(item.value)}
        selectedItem={allFullDateTimeFrameConditionTypeOptions.find(
          (o) => o.id === conditionType,
        )}
        size="xs"
      />
    );
  },
);

const BeforeTimeFrameSelector = memo(function BeforeTimeFrameSelector({
  condition,
  setCondition,
}: {
  condition: InProgress<BeforeTimeframeCondition>;
  setCondition: (condition: InProgress<BeforeTimeframeCondition>) => void;
}) {
  return (
    <RedoSingleDatePicker
      buttonSize="sm"
      date={
        condition.date
          ? // The interface for timeframe conditions says date is a Date,
            // but that's not the case in the automation builder because the
            // zod schema for automations uses z.any() for inline segments.
            // Better solution would be to fill out that schema.
            dateToPlainDateTime(new Date(condition.date)).toPlainDate()
          : undefined
      }
      setDate={(plainDate) => {
        setCondition({
          ...condition,
          date: plainDateTimeToDate(plainDate.toPlainDateTime()),
        });
      }}
    />
  );
});

const AfterTimeFrameSelector = memo(function AfterTimeFrameSelector({
  condition,
  setCondition,
}: {
  condition: InProgress<AfterTimeframeCondition>;
  setCondition: (condition: InProgress<AfterTimeframeCondition>) => void;
}) {
  return (
    <RedoSingleDatePicker
      buttonSize="sm"
      date={
        condition.date
          ? dateToPlainDateTime(new Date(condition.date)).toPlainDate()
          : undefined
      }
      maxDate={dateToPlainDateTime(new Date()).toPlainDate()}
      setDate={(plainDate) => {
        setCondition({
          ...condition,
          date: plainDateTimeToDate(plainDate.toPlainDateTime()),
        });
      }}
    />
  );
});

const BetweenDatesTimeFrameSelector = memo(
  function BetweenDatesTimeFrameSelector({
    condition,
    setCondition,
  }: {
    condition: InProgress<BetweenDatesTimeframeCondition>;
    setCondition: (
      condition: InProgress<BetweenDatesTimeframeCondition>,
    ) => void;
  }) {
    return (
      <RedoDateRangePicker
        dateRange={
          condition.range
            ? [
                dateToPlainDateTime(new Date(condition.range[0])).toPlainDate(),
                dateToPlainDateTime(new Date(condition.range[1])).toPlainDate(),
              ]
            : undefined
        }
        setDateRange={(range) => {
          setCondition({
            ...condition,
            range: range
              ? [
                  plainDateTimeToDate(range[0].toPlainDateTime()),
                  plainDateTimeToDate(range[1].toPlainDateTime()),
                ]
              : undefined,
          });
        }}
        size="sm"
      />
    );
  },
);

const BetweenRelativeTimeFrameSelector = memo(
  function BetweenRelativeTimeFrameSelector({
    condition,
    setCondition,
  }: {
    condition: InProgress<BetweenRelativeTimeframeCondition>;
    setCondition: (
      condition: InProgress<BetweenRelativeTimeframeCondition>,
    ) => void;
  }) {
    const { start, end, units } = condition;

    return (
      <>
        <TimeframeNumericInput
          setValue={(number) => {
            setCondition({ ...condition, start: number });
          }}
          value={start}
        />
        <SegmentBuilderText>and</SegmentBuilderText>
        <TimeframeNumericInput
          setValue={(number) => {
            setCondition({ ...condition, end: number });
          }}
          value={end}
        />
        <TimeframeUnitSelector
          isPlural={end !== 1}
          setTimeframeUnit={(unit) =>
            setCondition({ ...condition, units: unit })
          }
          timeframeUnit={units}
        />
      </>
    );
  },
);

const OnTimeFrameSelector = memo(function OnTimeFrameSelector({
  condition,
  setCondition,
}: {
  condition: InProgress<OnTimeframeCondition>;
  setCondition: (condition: InProgress<OnTimeframeCondition>) => void;
}) {
  return (
    <RedoSingleDatePicker
      buttonSize="sm"
      date={
        condition.date
          ? dateToPlainDateTime(new Date(condition.date)).toPlainDate()
          : undefined
      }
      setDate={(plainDate) => {
        setCondition({
          ...condition,
          date: plainDateTimeToDate(plainDate.toPlainDateTime()),
        });
      }}
    />
  );
});

const BeforeNowRelativeTimeFrameSelector = memo(
  function BeforeRelativeTimeFrameSelector({
    condition,
    setCondition,
  }: {
    condition: InProgress<BeforeNowRelativeTimeframeCondition>;
    setCondition: (
      condition: InProgress<BeforeNowRelativeTimeframeCondition>,
    ) => void;
  }) {
    const { value, units } = condition;

    return (
      <>
        <TimeframeNumericInput
          setValue={(number) => {
            setCondition({ ...condition, value: number });
          }}
          value={value}
        />
        <TimeframeUnitSelector
          isPlural={value !== 1}
          setTimeframeUnit={(unit) =>
            setCondition({ ...condition, units: unit })
          }
          timeframeUnit={units}
        />
      </>
    );
  },
);

const BeforeRelativeTimeFrameSelector = memo(
  function BeforeRelativeTimeFrameSelector({
    condition,
    setCondition,
  }: {
    condition: InProgress<BeforeNowRelativeTimeframeCondition>;
    setCondition: (
      condition: InProgress<BeforeNowRelativeTimeframeCondition>,
    ) => void;
  }) {
    const { value, units } = condition;

    return (
      <>
        <TimeframeNumericInput
          setValue={(number) => {
            setCondition({ ...condition, value: number });
          }}
          value={value}
        />
        <TimeframeUnitSelector
          isPlural={value !== 1}
          setTimeframeUnit={(unit) =>
            setCondition({ ...condition, units: unit })
          }
          timeframeUnit={units}
        />
        <SegmentBuilderText>ago</SegmentBuilderText>
      </>
    );
  },
);

const timeFrameUnitToFriendlySingular: Record<TimeframeUnit, string> = {
  [TimeframeUnit.HOUR]: "hour",
  [TimeframeUnit.DAY]: "day",
  [TimeframeUnit.WEEK]: "week",
  [TimeframeUnit.MONTH]: "month",
};

const timeFrameUnitToFriendlyPlural: Record<TimeframeUnit, string> = {
  [TimeframeUnit.HOUR]: "hours",
  [TimeframeUnit.DAY]: "days",
  [TimeframeUnit.WEEK]: "weeks",
  [TimeframeUnit.MONTH]: "months",
};

const TimeframeUnitSelector = memo(function TimeframeUnitSelector({
  isPlural,
  timeframeUnit,
  setTimeframeUnit,
}: {
  isPlural: boolean;
  timeframeUnit: TimeframeUnit | undefined;
  setTimeframeUnit: (unit: TimeframeUnit) => void;
}) {
  const timeFrameUnitOptions: RedoListItem<TimeframeUnit>[] = useMemo(
    () =>
      Object.values(TimeframeUnit).map((unit) => ({
        id: unit,
        type: "text",
        text: isPlural
          ? timeFrameUnitToFriendlyPlural[unit]
          : timeFrameUnitToFriendlySingular[unit],
        value: unit,
      })),
    [isPlural],
  );

  return (
    <RedoSingleSelectDropdownInput<TimeframeUnit>
      fitToAnchor={false}
      flexGrow={false}
      options={timeFrameUnitOptions}
      optionSelected={({ item }) => setTimeframeUnit(item.value)}
      selectedItem={timeFrameUnitOptions.find((o) => o.id === timeframeUnit)}
      size="xs"
    />
  );
});

const TimeframeNumericInput = memo(function TimeframeNumericInput({
  value,
  setValue,
}: {
  value: number | undefined;
  setValue: (value: number | undefined) => void;
}) {
  return (
    <NumericFilterInput
      dataTestId="timeframe-numeric-input"
      maxDigits={3}
      setValue={setValue}
      value={value}
    />
  );
});
